import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { SearchInput, PaginationBar } from "@/components/admin/ListControls";
import { useDebounced } from "@/hooks/useDebounced";
import { ORG_TYPE_LABELS } from "@/constants/business_status";

export default function OrganizationsPage() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const q = useDebounced(searchInput, 350);
  const [type, setType] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const initial = useRef(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (type !== "all") params.organization_type = type;
      if (includeArchived) params.include_archived = true;
      const r = await apiClient.get("/admin/organizations", { params });
      setItems(r.data.items); setTotal(r.data.total); setTotalPages(r.data.total_pages);
    } catch { toast.error("Erreur lors du chargement"); } finally { setLoading(false); }
  }, [q, type, includeArchived, page, pageSize]);

  useEffect(() => { if (admin) fetchData(); }, [admin, fetchData]);
  useEffect(() => { if (initial.current) { initial.current = false; return; } setPage(1); }, [q, type, includeArchived, pageSize]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;

  return (
    <AdminShell>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <SearchInput value={searchInput} onChange={setSearchInput} testId="orgs-search-input" placeholder="Rechercher nom légal, email…" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="orgs-filter-type" className="w-52 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            {Object.entries(ORG_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={includeArchived ? "yes" : "no"} onValueChange={(v) => setIncludeArchived(v === "yes")}>
          <SelectTrigger data-testid="orgs-filter-archived" className="w-44 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">Actifs uniquement</SelectItem>
            <SelectItem value="yes">Inclure archivés</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="orgs-refresh" onClick={fetchData} variant="outline" className="rounded-xl"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
        <Table data-testid="orgs-table">
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Contacts</TableHead>
              <TableHead>Dossiers</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Téléphone</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy" /></TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="orgs-empty">Aucune organisation</TableCell></TableRow>
            ) : items.map((o) => (
              <TableRow key={o.id} data-testid={`org-row-${o.id}`}>
                <TableCell className="font-medium">{o.legal_name}</TableCell>
                <TableCell><span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2.5 py-1">{ORG_TYPE_LABELS[o.organization_type] || o.organization_type}</span></TableCell>
                <TableCell className="text-center">{o.contacts_count}</TableCell>
                <TableCell className="text-center">{o.cases_count}</TableCell>
                <TableCell>{o.email || "—"}</TableCell>
                <TableCell>{o.phone || "—"}</TableCell>
                <TableCell><span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${o.archived_at ? "bg-gray-100 text-muted-foreground" : "bg-green-50 text-green-700"}`}>{o.archived_at ? "Archivée" : "Active"}</span></TableCell>
                <TableCell><Button data-testid={`org-view-${o.id}`} onClick={() => navigate(`/admin/organisations/${o.id}`)} variant="ghost" size="sm" className="text-navy font-semibold">Voir</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PaginationBar total={total} page={page} pageSize={pageSize} totalPages={totalPages} onPage={setPage} onSize={setPageSize} loading={loading} testPrefix="orgs-" />
    </AdminShell>
  );
}
